import { ServiceError } from "../stores/BaseTypes";
import type { CardholderNumber, Dictionary, Weight } from "../stores/BaseTypes";
import type { FulfillmentType, ServiceOption } from "./ArticleTypes";
import type { ArticlePrices, CheckoutPrices, DeliveryFees, GroupHeaderPrice, TotalPrices } from "./Prices";
import type { RealPaymentType } from "../stores/CheckoutTypes";
export type WeekDay = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
export type TimeSlot = {
	from: number | null | undefined;
	until: number | null | undefined;
	fromUTC?: number | null;
	untilUTC?: number | null;
};
export type DeliveryTimes = {
	routeId?: string | null;
	slots: Array<TimeSlot> | null | undefined;
	sequence?: ("HIGH_PRIORITY" | "NORMAL_PRIORITY") | null;
	priority?: number | null;
};
export type DeliveryTuple = {
	dayOfWeek: WeekDay;
	deliveryTimes: DeliveryTimes | null | undefined;
};
export type DeliveryList = {
	tupleList: Array<DeliveryTuple>;
};
export type AddressType = "INVOICE" | "DELIVERY" | "DELIVERY_MCFM" | "ADVERTISEMENT" | "STORE_PICKUP" | "HEAD_OFFICE";
export type DeliveryInterval = "WEEKLY" | "UNEVEN_WEEKS" | "EVEN_WEEKS";
export type CustomerType = "UNKNOWN" | "EMPLOYEE";
export type CustomerAddress = {
	hash: string;
	integrationId: string;
	house2Num: string | null | undefined;
	truckSize: string | null | undefined;
	deliveryStore: string | null | undefined;
	timeToLoad: string | null | undefined;
	city: string | null | undefined;
	houseNumber: string | null | undefined;
	faxNumber: string | null | undefined;
	geoCoordinates: string | null | undefined;
	state: string | null | undefined;
	zipcode: string | null | undefined;
	street: string | null | undefined;
	nonProductionRedirectUrl: string | null | undefined;
	buildingName: string | null | undefined;
	deliveryFrequency: DeliveryInterval;
	addressLine2: string | null | undefined;
	deliveries: DeliveryList;
	accessibleByCurrentUser: boolean;
	addressNumber: string | null | undefined;
	phoneNumber: string | null | undefined;
	country: string | null | undefined;
	addressType: AddressType;
	district: string | null | undefined;
};
export type WebshopAddress = {
	id: string;
	addressType: AddressType;
	country: string | null | undefined;
	postCode: string | null | undefined;
	city: string | null | undefined;
	district: string | null | undefined;
	allowedZipcode?: boolean | null;
	state: string | null | undefined;
	addressLine1: string | null | undefined;
	addressLine2: string | null | undefined;
	addressLine3: string | null | undefined;
	addressLine4: string | null | undefined;
};
export type CheckoutId = string;
export type CardholderData = {
	cardHolderNumber: CardholderNumber;
	customerId: string;
	sendOrderConfirmation: boolean;
	sendDeliveryNotification: boolean;
	lastName: string | null | undefined;
	firstName: string | null | undefined;
	middleName: string | null | undefined;
	email: string | null | undefined;
	phoneNumber: string | null | undefined;
	paymentsForCardholder: Array<RealPaymentType>;
	checkoutBlockingReasons?: string[] | null;
};
export type CheckoutCustomer = {
	house2Num: string | null | undefined;
	city: string | null | undefined;
	addressId: string | null | undefined;
	houseNumber: string | null | undefined;
	faxNumber: string | null | undefined;
	firstName: string | null | undefined;
	state: string | null | undefined;
	mayEnterOwnOrderNumber: boolean;
	zipcode: string | null | undefined;
	primaryCardholderNumber: string | null | undefined;
	cardholderById: Dictionary<CardholderData>;
	sendOrderConfirmation: boolean;
	street: string | null | undefined;
	customerId: string;
	customerNumber?: string | null;
	homeStore?: string | null;
	addressLine2: string | null | undefined;
	lastName: string | null | undefined;
	country: string | null | undefined;
	primaryServiceType: string | null | undefined;
	district: string | null | undefined;
	addresses: Dictionary<CustomerAddress>;
	primaryDeliveryAddressId: string;
	webshopAddresses: Dictionary<WebshopAddress>;
	customerType: CustomerType;
	generalComment?: string;
	pickingComment?: string;
	deliveryComment?: string;
	invoiceComment?: string;
	validForWeb: boolean;
	validForFsd: boolean;
	eligibleForEgalTax: boolean | null | undefined;
	blockedFromMetroCredit: boolean | null | undefined;
	checkoutBlockingReason?: string | null;
	checkoutBlockingReasons?: string[] | null;
	email: string | null | undefined;
	phoneNumber: string | null | undefined;
};
export function getCustomerName(customerData: CheckoutCustomer): string | null {
	const { firstName, lastName } = customerData;
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (firstName) return firstName;
	if (lastName) return lastName;
	return null;
}

export type CreditCardData = {
	cardholderName?: string | null;
	cardPan?: string | null;
	cardValidity: {
		year?: string | null;
		month?: string | null;
	};
	cardBrand?: string | null;
	cardToken?: string | null;
	cardIssuer?: string | null;
	country?: string | null;
	paymentToken?: string | null;
};
export type ReducedCreditCardData = {
	cardPan?: string | null;
	cardBrand?: string | null;
	cardToken?: string | null;
};
export type PaymentType = {
	paymentAllowance: string;
	paymentType: string | null | undefined;
	priority?: number | null;
};
export type PaymentSelectionDetails = PaymentType & {
	creditCardToken: string | null | undefined;
	resetCreditCard: boolean;
};
export type FullPaymentTypeWithAdditionalInfo = {
	paymentAllowance: string;
	paymentType: string | null | undefined;
	isPrimaryPayment?: boolean;
	paymentId?: number | null;
	priority?: number | null;
	temporaryNotAvailable?: boolean;
	unavailabilityReasons?: UnavailabilityReason[];
	transactional?: boolean;
};
export const UNAVAILABILITY_REASON_MISSING_PRICES = "MISSING_PRICES";
export const UNAVAILABILITY_REASON_BLOCKED = "BLOCKED";
export const UNAVAILABILITY_REASON_MIN_BASKET_VALUE = "MIN_BASKET_VALUE";
export type UnavailabilityReason = {
	reasonType: string;
	totalBelowThreshold?: number | null;
	threshold?: number | null;
	downtimeConfig?: DowntimeConfig | null;
};
export type PaymentData = {
	realPaymentType: PaymentType;
	creditCardData: CreditCardData | null | undefined;
	pspOrderId: string | null | undefined;
};
export const PRIMARY_PAYMENT_REFERENCE = "0";
export const SECONDARY_PAYMENT_REFERENCE = "1";
export type PaymentId = {
	allowance: string;
	type: string | null | undefined;
};
type CreditCardLocationId = string;
export type FullPayment = {
	id: PaymentId;
	creditCardData: CreditCardData | null | undefined;
	pspOrderId: string | null | undefined;
};
export type PaymentSelection = {
	mainPayment: FullPayment;
	mainFulfillmentTypes: FulfillmentType[];
	complementaryPayment: FullPayment | null | undefined;
	selectedCreditCardInfo: Record<CreditCardLocationId, CreditCardData>;
};
export type PaymentMethodSelectionDetails = {
	paymentId: PaymentId;
	creditCard: ReducedCreditCardData | null | undefined;
};
export type Timeslot = {
	fromUtc: string;
	untilUtc: string;
};
export type CheckoutPickupDate = {
	date: string;
	timeslot?: Timeslot | null;
	validUntil: string;
};
export type CheckoutHeader = {
	orderSource: OrderSource | null | undefined;
	singleInvoice: boolean | null | undefined;
	preferredDeliveryDate: string | null | undefined;
	preferredPickupDate: CheckoutPickupDate | null | undefined;
	serviceTypeSelection: ServiceTypeSelection;
	paymentSelection: PaymentSelection | null | undefined;
	comment: string | null | undefined;
	egalTaxApplied: boolean;
	ownOrderNumber: string | null | undefined;
	ownStoreNumber: string | null | undefined;
	ignoreFees: boolean;
	sendOrderConfirmationToCustomer: boolean;
	sendOrderConfirmationToCardholder: boolean;
	sendPickingListToCentralWarehouse: boolean | null | undefined;
	pickingCommentAddition: string | null | undefined;
	deliveryCommentAddition: string | null | undefined;
	invoiceCommentAddition: string | null | undefined;
	deliveryPhoneNumber?: string | null;
	fiscalReceipt?: boolean | null;
	emailAddress?: string | null;
	publicOfficeContractNumber?: string | null;
	publicOfficeProjectNumber?: string | null;
	voucher?: Redeemable | null;
	redeemLoyaltyPoints?: boolean | null;
};
type Redeemable = {
	epc: string;
	id: string;
};
export type OrderSource =
	| "PHONE_INCOMING_INT"
	| "PHONE_INCOMING_EXT"
	| "PHONE_OUTBOUND_INT"
	| "PHONE_OUTBOUND_EXT"
	| "EMAIL"
	| "EMAIL_CALL_CENTER"
	| "FAX_STORE"
	| "FAX_CALL_CENTER"
	| "CUSTOMERVISIT"
	| "DRIVER"
	| "STOREVISIT"
	| "WEBSHOP_INT"
	| "WEBSHOP_EXT"
	| "CUSTOMER"
	| "WE_CHAT"
	| "FOLLOW_UP_ORDER";
export type ServiceTypeOption = PickupOption | DeliveryOption;
export type PickupOption = {
	selected: boolean;
};
export type DeliveryOption = {
	selected: boolean;
};
export type ServiceType = {
	serviceType: ServiceTypeEnum;
	additionalOptions: Array<ServiceTypeOption>;
};
export type ServiceTypeSelection = {
	preferredServiceType: ServiceTypeEnum;
	additionalPickupOptions: PickupOption[] | null | undefined;
	additionalDeliveryOptions: DeliveryOption[] | null | undefined;
};
export type ServiceTypeEnum = "DELIVERY" | "PICKUP";
export type SuperGroupId = "FSD" | "CLICK_AND_COLLECT" | "PARCEL_DELIVERY" | "PARCEL_PICKUP";
type BaseGroupHeader = {
	deliveryDate: string | null | undefined;
	groupPrices: GroupHeaderPrice | null | undefined;
	headerId: string;
	serviceType: ServiceType;
	superGroupId?: SuperGroupId | null;
};
export type DeliveryDateFlag =
	| "VIOLATES_LEAD_TIME"
	| "CUSTOMER_SLOT"
	| "STORE_HOLIDAY"
	| "VIOLATES_CUTOFF_TIME"
	| "VIOLATES_HOLIDAY_CUTOFF_TIME"
	| "ALTERNATIVE_FOR_STORE_HOLIDAY";
export type DeliveryDateInfo = {
	valid: boolean;
	flags: DeliveryDateFlag[];
};
export type FsdGroupHeader = BaseGroupHeader & {
	fulfillmentType: "FSD";
};
export type WebshopGroupHeader = BaseGroupHeader & {
	// webshop fields
	fulfillmentType: "WEBSHOP";
};
export type DropShipmentGroupHeader = BaseGroupHeader & {
	fulfillmentType: "DROP_SHIPMENT";
	supplierInfo: SupplierInfo;
};
export type ClickAndCollectGroupHeader = BaseGroupHeader & {
	fulfillmentType: "CLICK_AND_COLLECT";
};
export type SupplierInfo = {
	id: string;
	name: string;
};
export type CheckoutGroupHeader =
	| FsdGroupHeader
	| WebshopGroupHeader
	| DropShipmentGroupHeader
	| ClickAndCollectGroupHeader;
export type CheckoutNonFsdGroupHeader = WebshopGroupHeader | DropShipmentGroupHeader | ClickAndCollectGroupHeader;
export type BaseCheckoutItem = {
	groupHeaderId: string;
	bundleId: string;
	quantity: number;
	possibleSuperGroupIds?: SuperGroupId[];
	weightPerPiece?: Weight | null;
	description: string;
	imageSources: {
		imageUrl1: string;
		imageUrl2: string;
		imageUrl3?: string;
		imageUrlS?: string;
	};
	serviceOptions: ServiceOption[] | null | undefined;
	prices: ArticlePrices | null | undefined;
	actualDeliveryDate: string | null | undefined;
	earliestDeliveryDate?: string | null;
	earliestPickupDate?: string | null;
	licenseType: string | null | undefined;
};
export type CheckoutItem = {
	bundleId: string;
	quantity: number;
	weightPerPiece?: Weight | null;
	description: string;
	imageSources: {
		imageUrl1: string;
		imageUrl2: string;
		imageUrl3?: string;
		imageUrlS?: string;
	};
	serviceOptions: ServiceOption[] | null | undefined;
	prices: ArticlePrices | null | undefined;
	headerReference: {
		value: string;
	};
	comment: string | null | undefined;
	displayId: string;
	subsys?: string | null;
	trackingId?: string | null;
	bundleSize?: string | null;
	customerDisplayId: string;
	possibleSuperGroupIds?: SuperGroupId[];
	variantDescription?: string;
	isWeighted?: boolean;
	categories?: Array<string> | null;
	ownBrand?: string | null;
	licenseType?: string | null;
	earliestDeliveryDate?: string | null;
	earliestPickupDate?: string | null;
};
export type CheckoutContext = {
	country: string;
	storeId: string;
	customerId: string;
	cardholderId: string;
	employeeId: string | null | undefined;
	cartId: string;
	cartVersion: number;
};
export type CheckoutStatusType = "IN_PROGRESS" | "PAYMENT_PENDING" | "SUBMITTED" | "HIPAY_PAYMENT_PENDING";
export type CheckoutStatus = {
	type: CheckoutStatusType;
	submitId?: string;
	backendSetting?: string;
	timestamp: number;
};
export type PaymentMethodId = {
	allowance: string;
	type: string | null | undefined;
};
export type DowntimeConfig = {
	start: string | null | undefined;
	end: string | null | undefined;
};
export type CheckoutData = {
	id: CheckoutId;
	version: number;
	context: CheckoutContext;
	status: CheckoutStatus;
	customerData: CheckoutCustomer;
	selectedDeliveryAddressId: string | null | undefined;
	selectedPickupStoreId: string | null | undefined;
	savedCreditCardsLocationId: string | null | undefined;
	header: CheckoutHeader;
	groupHeaders: Dictionary<CheckoutGroupHeader>;
	items: Record<string, CheckoutItem>;
	checkoutPrices: CheckoutPrices;
	availableLoyaltyPoints: number | null;
};
export type InitialEmployeeCheckoutHeader = {
	deliveryDate?: string | null;
	paymentType?: PaymentType | null;
	orderSource?: OrderSource | null;
	serviceType?: ServiceTypeEnum | null;
	ownOrderNumber?: string | null;
	ownStoreNumber?: string | null;
	cardholderNumber?: string | null;
	sendOrderConfirmationToCustomer?: boolean | null;
	sendOrderConfirmationToCardholder?: boolean | null;
	singleOrder?: boolean | null;
	customerDataAdditions?: {
		pickingComment?: string | null;
		deliveryComment?: string | null;
		invoiceComment?: string | null;
	};
	ignoreFees?: boolean | null;
	sendPickingListToCentralWarehouse?: boolean | null;
	cartComment?: string | null;
	applyEgalisationTax?: boolean;
};
export function alternativeServiceType(serviceType: ServiceTypeEnum): ServiceTypeEnum {
	return serviceType === "DELIVERY" ? "PICKUP" : "DELIVERY";
}
// migrate to isHipayCreditCardPayment in BOC-61772 (also move these to CheckoutUtil then, where Metro Credit function is)
export function isHipayCreditCard(paymentMethod: PaymentType): boolean {
	return isHipayCreditCardPayment(paymentMethod.paymentAllowance, paymentMethod.paymentType);
}
export function isHipayCreditCardPaymentMethod(paymentId: PaymentId): boolean {
	return isHipayCreditCardPayment(paymentId.allowance, paymentId.type);
}
export function isHipayCreditCardPayment(allowance: string, type: string | null | undefined): boolean {
	return allowance === "PAYMENT_ON_DELIVERY" && type === "WEBSHOP_CREDIT_CARD";
}

export type HipayPreauthorizePaymentType = "PAYPAL" | "WEBSHOP_CREDIT_CARD";

function required<T>(value: any, fieldName: string): T {
	if (typeof value === "undefined" || value === null) {
		throw new ServiceError({
			message: `Required field ${fieldName} was ${JSON.stringify(value)}.`,
			code: "OC-998",
			type: "Parsing",
		});
	}

	return value;
}

function parseCheckoutPrices(checkoutPricesJson: Record<string, any> | null | undefined): CheckoutPrices {
	if (!checkoutPricesJson) {
		return {
			total: null,
			deliveryFees: null,
			superGroupTotals: {},
			byMethod: null,
			byPayment: {
				pricesMainPayment: null,
				pricesRemaining: null,
			},
			vouchers: [],
		};
	}

	return {
		total: checkoutPricesJson.total as TotalPrices | null | undefined,
		deliveryFees: checkoutPricesJson.deliveryFees as DeliveryFees | null | undefined,
		superGroupTotals: checkoutPricesJson.superGroupTotals,
		byMethod: checkoutPricesJson.byMethod,
		byPayment: checkoutPricesJson.byPayment,
		vouchers: checkoutPricesJson.vouchers,
	};
}

function parseStatus(statusJson: Record<string, any>): CheckoutStatus {
	const status: CheckoutStatus = {
		type: statusJson.type,
		timestamp: statusJson.timestamp,
	};

	if (statusJson.submitId) {
		status.submitId = statusJson.submitId;
	}

	if (statusJson.backendSetting) {
		status.backendSetting = statusJson.backendSetting;
	}

	return status;
}

export function parseCheckoutData(json: Record<string, any>): CheckoutData {
	if (!json || typeof json !== "object") {
		throw new ServiceError({
			message: "Json for CheckoutData is not of object type.",
			code: "OC-998",
			type: "Parsing",
		});
	}

	return {
		id: required(json.id, "id"),
		version: required(json.version, "version"),
		context: json.context as CheckoutContext,
		status: parseStatus(json.status),
		customerData: json.customerData as CheckoutCustomer,
		header: json.header as CheckoutHeader,
		groupHeaders: json.groupHeaders as Dictionary<CheckoutGroupHeader>,
		items: json.items as Record<string, CheckoutItem>,
		checkoutPrices: parseCheckoutPrices(json.checkoutPrices),
		selectedDeliveryAddressId: json.selectedDeliveryAddressId,
		selectedPickupStoreId: json.selectedPickupStoreId,
		savedCreditCardsLocationId: json.savedCreditCardsLocationId,
		availableLoyaltyPoints: json.availableLoyaltyPoints,
	};
}

export function convertToBaseCheckoutItem(
	checkoutItem: CheckoutItem,
	actualDeliveryDate?: string | null,
): BaseCheckoutItem {
	return {
		groupHeaderId: checkoutItem.headerReference.value,
		bundleId: checkoutItem.bundleId,
		quantity: checkoutItem.quantity,
		possibleSuperGroupIds: checkoutItem.possibleSuperGroupIds,
		weightPerPiece: checkoutItem.weightPerPiece,
		description: checkoutItem.description,
		imageSources: checkoutItem.imageSources,
		serviceOptions: checkoutItem.serviceOptions,
		prices: checkoutItem.prices,
		earliestDeliveryDate: checkoutItem.earliestDeliveryDate,
		earliestPickupDate: checkoutItem.earliestPickupDate,
		actualDeliveryDate: actualDeliveryDate,
		licenseType: checkoutItem.licenseType,
	};
}
export type PaymentReasonInfo = {
	showPayment: boolean;
	unavailabilityReason?: UnavailabilityReason;
};
export type PaymentWithAvailability = PaymentReasonInfo & {
	payment: PaymentType;
};
